/**
 * Toolbar buttons. Each reads the editor from Tiptap's context and its own
 * presentation from `EditorToolbarContext` — so a button takes no props at
 * all, and composing it into a different region changes how it looks without
 * touching the call site.
 */
import type { Editor } from "@tiptap/core";
import { useCurrentEditor, useEditorState } from "@tiptap/react";
import {
  BoldIcon,
  CheckIcon,
  Heading1Icon,
  Heading2Icon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  type LucideIcon,
  type LucideProps,
  StrikethroughIcon,
  TextIcon,
  TextQuoteIcon,
  UnderlineIcon,
} from "lucide-react";
import { type ReactNode, use } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { EditorToolbarContext } from "./toolbar-context";

type EditorButtonProps = {
  name: string;
  isActive: () => boolean;
  command: () => void;
  icon: LucideIcon | ((props: LucideProps) => ReactNode);
};

const ToolbarButton = ({ name, isActive, command, icon: Icon }: EditorButtonProps) => {
  const { editor } = useCurrentEditor();
  const { labels, onCommand } = use(EditorToolbarContext);

  // `useCurrentEditor` returns a stable instance and subscribes to nothing, so
  // calling isActive() during render evaluates it once at mount and never
  // again — the toggles never lit up. `useEditorState` re-runs the selector per
  // transaction, which is what makes the active state track the selection.
  const active = useEditorState({ editor, selector: () => isActive() }) ?? false;

  // A labelled row is a menu item and wants the name plus a tick. A bare icon
  // has nowhere to put a tick without growing the button, so it carries its
  // active state as a filled background instead.
  return (
    <Button
      aria-label={labels ? undefined : name}
      aria-pressed={labels ? undefined : active}
      className={
        labels
          ? "h-7 w-full justify-start gap-2 px-2"
          : cn("size-7 gap-0 p-0", active && "bg-accent text-accent-foreground")
      }
      onClick={() => {
        command();
        onCommand?.();
      }}
      size="sm"
      variant="ghost"
    >
      <Icon className={cn("shrink-0", labels ? "text-muted-foreground" : "text-foreground")} size={14} />
      {labels && <span className="flex-1 text-left">{name}</span>}
      {labels && active ? <CheckIcon className="shrink-0 text-muted-foreground" size={12} /> : null}
    </Button>
  );
};

/**
 * A list item wraps a paragraph, so `isActive("paragraph")` is true inside a
 * list as well — the list checks are what stop "Text" claiming one.
 */
export const isPlainTextBlock = (editor: Editor) =>
  editor.isActive("paragraph") && !editor.isActive("bulletList") && !editor.isActive("orderedList");

export const EditorNodeText = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleNode("paragraph", "paragraph").run()}
      icon={TextIcon}
      isActive={() => isPlainTextBlock(editor)}
      name="Text"
    />
  );
};

export const EditorNodeHeading1 = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      icon={Heading1Icon}
      isActive={() => editor.isActive("heading", { level: 1 }) ?? false}
      name="Heading 1"
    />
  );
};

export const EditorNodeHeading2 = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      icon={Heading2Icon}
      isActive={() => editor.isActive("heading", { level: 2 }) ?? false}
      name="Heading 2"
    />
  );
};

export const EditorNodeBulletList = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleBulletList().run()}
      icon={ListIcon}
      isActive={() => editor.isActive("bulletList") ?? false}
      name="Bullet List"
    />
  );
};

export const EditorNodeOrderedList = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleOrderedList().run()}
      icon={ListOrderedIcon}
      isActive={() => editor.isActive("orderedList") ?? false}
      name="Numbered List"
    />
  );
};

export const EditorNodeQuote = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleNode("paragraph", "paragraph").toggleBlockquote().run()}
      icon={TextQuoteIcon}
      isActive={() => editor.isActive("blockquote") ?? false}
      name="Quote"
    />
  );
};

export const EditorFormatBold = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleBold().run()}
      icon={BoldIcon}
      isActive={() => editor.isActive("bold") ?? false}
      name="Bold"
    />
  );
};

export const EditorFormatItalic = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleItalic().run()}
      icon={ItalicIcon}
      isActive={() => editor.isActive("italic") ?? false}
      name="Italic"
    />
  );
};

export const EditorFormatStrike = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleStrike().run()}
      icon={StrikethroughIcon}
      isActive={() => editor.isActive("strike") ?? false}
      name="Strikethrough"
    />
  );
};

export const EditorFormatUnderline = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <ToolbarButton
      command={() => editor.chain().focus().toggleUnderline().run()}
      icon={UnderlineIcon}
      isActive={() => editor.isActive("underline") ?? false}
      name="Underline"
    />
  );
};
